import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Tag, Check, MessageCircle, Flame, FileDown, Wand2, Loader2, X, Send, Search,
} from "lucide-react";
import { api, API } from "../lib/api";
import { SECTORS, SECTOR_LABELS } from "../data/wizardConfig";
import { toast } from "sonner";
import NltFilters from "../components/NltFilters";
import { matchesNltFilters } from "../lib/nltFilters";

export default function OffersCatalog() {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sector, setSector] = useState("all");
    const [query, setQuery] = useState("");
    const [nltFilters, setNltFilters] = useState({ durata: [], km: [], rata: [] });

    useEffect(() => {
        api.get("/offers")
            .then(({ data }) => setOffers(data))
            .finally(() => setLoading(false));
    }, []);

    const showSearch = sector === "noleggio_lungo_termine" || sector === "all";
    const showNltFilters = sector === "noleggio_lungo_termine";

    const filtered = useMemo(() => {
        let list = sector === "all" ? offers : offers.filter((o) => o.sector === sector);
        const q = query.trim().toLowerCase();
        if (q && showSearch) {
            list = list.filter((o) => {
                const haystack = [
                    o.title, o.subtitle, o.description,
                    ...(o.highlights || []),
                ].filter(Boolean).join(" ").toLowerCase();
                return haystack.includes(q);
            });
        }
        if (showNltFilters) {
            list = list.filter((o) => matchesNltFilters(o, nltFilters));
        }
        return list;
    }, [offers, sector, query, showSearch, showNltFilters, nltFilters]);

    return (
        <div className="max-w-6xl mx-auto" data-testid="offers-catalog-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
                <div>
                    <div className="inline-flex items-center gap-2 bg-[#C7FF2C]/30 text-[#0C4650] px-3 py-1 rounded-full text-xs font-bold">
                        <Tag className="w-3 h-3" /> CATALOGO OFFERTE
                    </div>
                    <h1 className="mt-3 text-3xl sm:text-4xl font-black text-[#0C4650]">
                        Offerte attive
                    </h1>
                    <p className="text-gray-600 max-w-xl mt-1">
                        Parti da un'offerta pre-configurata e personalizzala per il tuo cliente.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <FilterBtn active={sector === "all"} onClick={() => setSector("all")} label="Tutte" />
                    {Object.values(SECTORS).map((s) => (
                        <FilterBtn
                            key={s.key}
                            active={sector === s.key}
                            onClick={() => setSector(s.key)}
                            label={s.label}
                        />
                    ))}
                </div>
            </div>

            {showSearch && (
                <div className="mb-5 relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Cerca per modello, marca o caratteristica…"
                        className="w-full pl-12 pr-12 py-3.5 rounded-full border-2 border-gray-200 bg-white text-[#0C4650] font-medium placeholder:text-gray-400 focus:border-[#0C4650] focus:outline-none transition-all"
                        data-testid="offers-search-input"
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                            data-testid="offers-search-clear"
                            title="Cancella ricerca"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    )}
                </div>
            )}

            {showNltFilters && (
                <NltFilters
                    filters={nltFilters}
                    setFilters={setNltFilters}
                    totalMatches={filtered.length}
                />
            )}

            {loading ? (
                <div className="py-12 text-center text-gray-400">Caricamento…</div>
            ) : filtered.length === 0 ? (
                <div className="card p-12 text-center text-gray-500" data-testid="offers-empty-state">
                    {query.trim()
                        ? `Nessun risultato per "${query.trim()}"`
                        : "Nessuna offerta disponibile."}
                </div>
            ) : (
                <>
                    {showSearch && query.trim() && (
                        <p className="text-sm text-gray-500 mb-3" data-testid="offers-results-count">
                            {filtered.length} risultat{filtered.length === 1 ? "o" : "i"} per <b className="text-[#0C4650]">"{query.trim()}"</b>
                        </p>
                    )}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filtered.map((o) => (
                            <OfferCard key={o.id} offer={o} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

const FilterBtn = ({ active, onClick, label }) => (
    <button
        onClick={onClick}
        data-testid={`offer-filter-${label.replace(/\s+/g, "_")}`}
        className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
            active
                ? "bg-[#0C4650] text-white"
                : "bg-white border border-gray-200 text-[#0C4650] hover:border-[#0C4650]"
        }`}
    >
        {label}
    </button>
);

const OfferCard = ({ offer }) => {
    const total = offer.usage_total || 0;
    const last30 = offer.usage_30d || 0;
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [showQuick, setShowQuick] = useState(false);

    const shareWhatsApp = () => {
        const lines = [
            `Ciao! Ti propongo questa soluzione:`,
            ``,
            `*${offer.title}*`,
            offer.subtitle || "",
            ``,
            offer.description,
            ``,
            offer.monthly_price ? `💰 ${offer.monthly_price}` : "",
            offer.price && !offer.monthly_price ? `💰 ${offer.price}` : "",
            ...(offer.highlights || []).map((h) => `✓ ${h}`),
            ``,
            `Fammi sapere se vuoi approfondire!`,
        ]
            .filter(Boolean)
            .join("\n");
        window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, "_blank");
    };

    const downloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const resp = await api.get(`/offers/${offer.id}/pdf`, {
                responseType: "blob",
                timeout: 30000,
            });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Offerta-${(offer.title || "offerta").slice(0, 40)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 800);
            toast.success("PDF generato");
        } catch (e) {
            toast.error("Errore generazione PDF");
        } finally {
            setDownloadingPdf(false);
        }
    };

    const imgUrl = offer.image_url && offer.image_url.includes("/api/files/")
        ? `${offer.image_url}${offer.image_url.includes("?") ? "&" : "?"}token=${encodeURIComponent(localStorage.getItem("scintilla_token") || "")}`
        : offer.image_url;

    return (
        <div
            className="card overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all flex flex-col relative"
            data-testid={`offer-card-${offer.id}`}
        >
            {last30 >= 3 && (
                <div
                    className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-[#0C4650] text-[#C7FF2C] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg"
                    data-testid={`offer-hot-${offer.id}`}
                >
                    <Flame className="w-3 h-3" /> Hot
                </div>
            )}
            {imgUrl && (
                <div
                    className="h-44 bg-cover bg-center bg-[#0C4650]"
                    style={{ backgroundImage: `url(${imgUrl})` }}
                />
            )}
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">
                        {(SECTOR_LABELS[offer.sector] || SECTORS[offer.sector]?.label)?.toUpperCase()}
                    </div>
                    {total > 0 && (
                        <div
                            className="text-[10px] font-bold text-[#0C4650] bg-[#C7FF2C]/40 px-2 py-0.5 rounded-full"
                            data-testid={`offer-usage-${offer.id}`}
                        >
                            Usata {total} volt{total === 1 ? "a" : "e"}
                            {last30 > 0 && ` · ${last30} questo mese`}
                        </div>
                    )}
                </div>
                <h3 className="mt-1 text-xl font-black text-[#0C4650] leading-tight">
                    {offer.title}
                </h3>
                {offer.subtitle && <div className="text-sm text-gray-500">{offer.subtitle}</div>}
                <p className="text-sm text-gray-700 mt-3 line-clamp-3 flex-1">
                    {offer.description}
                </p>

                {(offer.monthly_price || offer.price) && (
                    <div className="mt-4 flex items-baseline gap-2">
                        {offer.monthly_price && (
                            <span className="text-2xl font-black text-[#0C4650]">
                                {offer.monthly_price}
                            </span>
                        )}
                        {offer.price && !offer.monthly_price && (
                            <span className="text-2xl font-black text-[#0C4650]">{offer.price}</span>
                        )}
                        {offer.price && offer.monthly_price && (
                            <span className="text-xs text-gray-400 line-through">{offer.price}</span>
                        )}
                    </div>
                )}

                {offer.highlights?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm">
                        {offer.highlights.slice(0, 3).map((h, i) => (
                            <li key={`${i}-${h.slice(0, 20)}`} className="flex items-start gap-2 text-gray-700">
                                <Check className="w-4 h-4 text-[#0C4650] mt-0.5 flex-shrink-0" />
                                <span>{h}</span>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="mt-5 grid grid-cols-3 gap-2">
                    <button
                        onClick={shareWhatsApp}
                        className="inline-flex items-center justify-center gap-1 bg-[#25D366] text-white font-bold px-2 py-2.5 rounded-lg hover:bg-[#1cb456] transition-all text-xs"
                        data-testid={`offer-whatsapp-${offer.id}`}
                        title="Condividi su WhatsApp"
                    >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </button>
                    <button
                        onClick={downloadPdf}
                        disabled={downloadingPdf}
                        className="inline-flex items-center justify-center gap-1 bg-[#0C4650] text-white font-bold px-2 py-2.5 rounded-lg hover:bg-[#062f37] transition-all text-xs disabled:opacity-50"
                        data-testid={`offer-pdf-${offer.id}`}
                        title="Scarica PDF accattivante"
                    >
                        {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                        PDF
                    </button>
                    <button
                        onClick={() => setShowQuick(true)}
                        className="inline-flex items-center justify-center gap-1 bg-[#C7FF2C] text-[#0C4650] font-bold px-2 py-2.5 rounded-lg hover:bg-[#b0eb20] transition-all text-xs"
                        data-testid={`offer-modify-${offer.id}`}
                        title="Modifica e richiedi variante"
                    >
                        <Wand2 className="w-3.5 h-3.5" /> Modifica
                    </button>
                </div>
            </div>
            {showQuick && (
                <QuickModifyModal
                    offer={offer}
                    onClose={() => setShowQuick(false)}
                />
            )}
        </div>
    );
};

const QuickModifyModal = ({ offer, onClose }) => {
    const [form, setForm] = useState({
        client_name: "",
        client_phone: "",
        client_email: "",
        custom_title: offer.title || "",
        custom_subtitle: offer.subtitle || "",
        custom_description: offer.description || "",
        custom_price: offer.price || "",
        custom_monthly_price: offer.monthly_price || "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!form.client_name.trim()) {
            toast.error("Il nome del cliente è obbligatorio");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/offers/${offer.id}/quick-request`, {
                client_name: form.client_name.trim(),
                client_phone: form.client_phone.trim() || null,
                client_email: form.client_email.trim() || null,
                custom_title: form.custom_title?.trim() || null,
                custom_subtitle: form.custom_subtitle?.trim() || null,
                custom_description: form.custom_description?.trim() || null,
                custom_price: form.custom_price?.trim() || null,
                custom_monthly_price: form.custom_monthly_price?.trim() || null,
                notes: form.notes?.trim() || null,
            });
            toast.success("Richiesta inviata alla sede");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
             data-testid="quick-modify-modal">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
                <div className="p-5 sm:p-6 border-b sticky top-0 bg-white flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">
                            VARIANTE OFFERTA
                        </div>
                        <h3 className="text-lg font-black text-[#0C4650] leading-tight">
                            Modifica e richiedi
                        </h3>
                    </div>
                    <button onClick={onClose} className="btn-ghost !px-2" data-testid="quick-modify-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 sm:p-6 space-y-4">
                    <div className="rounded-xl bg-[#f7f7f5] p-3 text-xs text-gray-600">
                        Partendo da <strong className="text-[#0C4650]">{offer.title}</strong>,
                        modifica i campi che ti interessano. La sede riceverà la richiesta e
                        ti tornerà con la proposta definitiva.
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <FieldS label="Nome cliente *" testid="qm-client-name">
                            <input className="field"
                                   value={form.client_name}
                                   onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                                   placeholder="Mario Rossi" />
                        </FieldS>
                        <FieldS label="Telefono" testid="qm-client-phone">
                            <input className="field"
                                   value={form.client_phone}
                                   onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                                   placeholder="+39 333 1234567" />
                        </FieldS>
                        <div className="sm:col-span-2">
                            <FieldS label="Email cliente" testid="qm-client-email">
                                <input type="email" className="field"
                                       value={form.client_email}
                                       onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                                       placeholder="cliente@email.com" />
                            </FieldS>
                        </div>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-2">
                            CARATTERISTICHE DESIDERATE (override sull'offerta base)
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <FieldS label="Titolo" testid="qm-title">
                                <input className="field"
                                       value={form.custom_title}
                                       onChange={(e) => setForm({ ...form, custom_title: e.target.value })} />
                            </FieldS>
                            <FieldS label="Sottotitolo" testid="qm-subtitle">
                                <input className="field"
                                       value={form.custom_subtitle}
                                       onChange={(e) => setForm({ ...form, custom_subtitle: e.target.value })} />
                            </FieldS>
                            <FieldS label="Prezzo" testid="qm-price">
                                <input className="field"
                                       value={form.custom_price}
                                       onChange={(e) => setForm({ ...form, custom_price: e.target.value })} />
                            </FieldS>
                            <FieldS label="Rata mensile" testid="qm-monthly">
                                <input className="field"
                                       value={form.custom_monthly_price}
                                       onChange={(e) => setForm({ ...form, custom_monthly_price: e.target.value })} />
                            </FieldS>
                        </div>
                        <div className="mt-3">
                            <FieldS label="Descrizione personalizzata" testid="qm-desc">
                                <textarea rows={3} className="field"
                                          value={form.custom_description}
                                          onChange={(e) => setForm({ ...form, custom_description: e.target.value })} />
                            </FieldS>
                        </div>
                    </div>

                    <FieldS label="Note per la sede" testid="qm-notes">
                        <textarea rows={3} className="field"
                                  placeholder="Es: il cliente ha bisogno di una rata più bassa, vuole 60 mesi invece di 48…"
                                  value={form.notes}
                                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </FieldS>

                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={onClose} className="btn-ghost" data-testid="qm-cancel">
                            Annulla
                        </button>
                        <button onClick={submit} disabled={submitting}
                                className="btn-primary disabled:opacity-50"
                                data-testid="qm-submit">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Invia richiesta
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FieldS = ({ label, children, testid }) => (
    <div data-testid={testid}>
        <label className="label">{label}</label>
        {children}
    </div>
);
